import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuditorDashboard = ({ metrics, recentActivity, onNavigate }) => {
  const { t } = useTranslation();
  const getStatusColor = (score) => {
    if (score >= 95) return 'text-green-600';
    if (score >= 85) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusIcon = (score) => {
    if (score >= 95) return 'CheckCircle';
    if (score >= 85) return 'AlertCircle';
    return 'XCircle';
  };

  return (
    <div className="space-y-8">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} className="text-primary" />
            </div>
            <Icon name="TrendingUp" size={16} className="text-green-500" />
          </div>
          <p className="text-2xl font-bold text-foreground">{metrics?.totalReports}</p>
          <p className="text-sm text-muted-foreground">Total Reports</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name={getStatusIcon(metrics?.complianceScore)} size={20} className={getStatusColor(metrics?.complianceScore)} />
            </div>
            <div className="text-right">
              <span className={`text-xs font-medium ${getStatusColor(metrics?.complianceScore)}`}>
                {metrics?.complianceScore >= 95 ? 'Excellent' : metrics?.complianceScore >= 85 ? 'Good' : 'Needs Review'}
              </span>
            </div>
          </div>
          <p className="text-2xl font-bold text-foreground">{metrics?.complianceScore}%</p>
          <p className="text-sm text-muted-foreground">Compliance Score</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Clock" size={20} className="text-warning" />
            </div>
            {metrics?.pendingReviews > 0 && (
              <div className="w-2 h-2 bg-warning rounded-full"></div>
            )}
          </div>
          <p className="text-2xl font-bold text-foreground">{metrics?.pendingReviews}</p>
          <p className="text-sm text-muted-foreground">Pending Reviews</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
              <Icon name="Shield" size={20} className="text-green-500" />
            </div>
            <Icon name="TrendingUp" size={16} className="text-green-500" />
          </div>
          <p className="text-2xl font-bold text-foreground">{metrics?.dataIntegrityScore}%</p>
          <p className="text-sm text-muted-foreground">Data Integrity</p>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Recent Audit Activity</h3>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => onNavigate?.('trail')}
            >
              View All
            </Button>
          </div>
        </div>
        
        <div className="divide-y divide-border">
          {recentActivity?.map((activity, index) => (
            <div key={activity?.id || index} className="p-4 hover:bg-muted/50">
              <div className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activity?.action === 'REPORT_VERIFIED' ? 'bg-green-500/10' :
                  activity?.action === 'DATA_EXPORTED'? 'bg-blue-500/10' : 'bg-accent/10'
                }`}>
                  <Icon 
                    name={
                      activity?.action === 'REPORT_VERIFIED' ? 'CheckCircle' :
                      activity?.action === 'DATA_EXPORTED'? 'Download' : 'Eye'
                    } 
                    size={14} 
                    className={
                      activity?.action === 'REPORT_VERIFIED' ? 'text-green-500' :
                      activity?.action === 'DATA_EXPORTED'? 'text-blue-500' : 'text-accent'
                    }
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-foreground truncate">
                      {activity?.resource || activity?.details}
                    </p>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        activity?.integrity === 'VERIFIED' ? 'bg-green-100 text-green-800' :
                        activity?.integrity === 'SYSTEM_VERIFIED'? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {activity?.integrity || 'LOGGED'}
                      </span>
                      <time className="text-xs text-muted-foreground">
                        {new Date(activity?.timestamp)?.toLocaleTimeString()}
                      </time>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {activity?.user} • {activity?.action?.replace(/_/g, ' ')?.toLowerCase()}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} className="text-primary" />
            </div>
            <h4 className="font-semibold text-foreground">Compliance Reports</h4>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Access all compliance reports with verified checksums and data integrity indicators.
          </p>
          <Button 
            variant="outline" 
            size="sm" 
            iconName="ArrowRight" 
            iconPosition="right"
            onClick={() => onNavigate?.('reports')}
            className="w-full"
          >
            View Reports
          </Button>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Activity" size={20} className="text-accent" />
            </div>
            <h4 className="font-semibold text-foreground">{t('auditor.tabs.auditTrail')}</h4>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Complete chronological log of all system activities with forensic-level detail.
          </p>
          <Button 
            variant="outline" 
            size="sm" 
            iconName="ArrowRight" 
            iconPosition="right"
            onClick={() => onNavigate?.('trail')}
            className="w-full"
          >
            View Trail
          </Button>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
              <Icon name="Download" size={20} className="text-green-500" />
            </div>
            <h4 className="font-semibold text-foreground">Export Package</h4>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Generate audit evidence packages with immutable checksums for external documentation.
          </p>
          <Button 
            variant="outline" 
            size="sm" 
            iconName="ArrowRight" 
            iconPosition="right"
            onClick={() => onNavigate?.('documents')}
            className="w-full"
          >
            Create Package
          </Button>
        </div>
      </div>

      {/* Data Freshness Indicator */}
      <div className="bg-muted/30 border border-border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Last updated: {new Date(metrics?.lastUpdate)?.toLocaleString()}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-xs text-green-600 font-medium">Data Current</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditorDashboard;